import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = ({ toggleCart, cartCount }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-[#F9F8F6]/95 backdrop-blur-md py-4 shadow-[0_1px_3px_rgba(0,0,0,0.05)]' : 'bg-transparent py-6 lg:py-8'}`}>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 flex justify-between items-center">
          
          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden p-2 -ml-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close Menu" : "Open Menu"}
            aria-expanded={mobileMenuOpen}
          >
            <SafeIcon icon={mobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className={`w-6 h-6 transition-colors duration-300 ${isScrolled || mobileMenuOpen ? 'text-[#2C2C2C]' : 'text-white'}`} />
          </button>

          {/* Desktop Links Left */}
          <div className={`hidden md:flex space-x-10 text-sm tracking-[0.1em] transition-colors duration-300 ${isScrolled ? 'text-[#2C2C2C]' : 'text-white'}`}>
            <a href="#lookbook" className="nav-link uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm px-1">Lookbook</a>
            <a href="#bundles" className="nav-link uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm px-1">Bundles</a>
            <a href="#journal" className="nav-link uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm px-1">Journal</a>
          </div>

          {/* Logo */}
          <a href="#" aria-label="CozyNest Home" className={`text-2xl lg:text-3xl font-serif tracking-[0.15em] absolute left-1/2 transform -translate-x-1/2 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm px-2 ${isScrolled || mobileMenuOpen ? 'text-[#2C2C2C]' : 'text-white'}`}>
            COZYNEST
          </a>

          {/* Icons Right */}
          <div className={`flex space-x-6 items-center transition-colors duration-300 ${isScrolled || mobileMenuOpen ? 'text-[#2C2C2C]' : 'text-white'}`}>
            <button aria-label="Search" className="hidden md:block hover:opacity-70 transition-opacity p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm">
              <SafeIcon icon={FiIcons.FiSearch} className="w-5 h-5" />
            </button>
            <button aria-label="Account" className="hidden md:block hover:opacity-70 transition-opacity p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm">
              <SafeIcon icon={FiIcons.FiUser} className="w-5 h-5" />
            </button>
            <button 
              onClick={toggleCart} 
              aria-label={`Cart, ${cartCount} items`}
              className="hover:opacity-70 transition-opacity relative p-1 flex items-center group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm"
            >
              <SafeIcon icon={FiIcons.FiShoppingBag} className="w-5 h-5" />
              <AnimatePresence>
                {cartCount > 0 && (
                  <motion.span 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className={`absolute -top-1 -right-2 text-[10px] w-[18px] h-[18px] rounded-full flex items-center justify-center font-medium transition-colors duration-300 ${isScrolled || mobileMenuOpen ? 'bg-[#2C2C2C] text-white' : 'bg-white text-[#2C2C2C]'}`}
                  >
                    {cartCount}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 z-40 bg-[#F9F8F6] pt-24 px-6 flex flex-col md:hidden"
            id="mobile-menu"
          >
            <div className="flex flex-col space-y-8 text-center mt-12">
              <a href="#lookbook" onClick={() => setMobileMenuOpen(false)} className="text-2xl font-serif text-[#2C2C2C] tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm py-1">Lookbook</a>
              <a href="#bundles" onClick={() => setMobileMenuOpen(false)} className="text-2xl font-serif text-[#2C2C2C] tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm py-1">Bundles</a>
              <a href="#journal" onClick={() => setMobileMenuOpen(false)} className="text-2xl font-serif text-[#2C2C2C] tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm py-1">Journal</a>
            </div>
            
            <div className="mt-auto pb-12 flex flex-col items-center space-y-6">
              <div className="flex space-x-8">
                <button aria-label="Search" className="text-[#2C2C2C] flex flex-col items-center space-y-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm p-2">
                  <SafeIcon icon={FiIcons.FiSearch} className="w-6 h-6" />
                  <span className="text-xs uppercase tracking-widest">Search</span>
                </button>
                <button aria-label="Account" className="text-[#2C2C2C] flex flex-col items-center space-y-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm p-2">
                  <SafeIcon icon={FiIcons.FiUser} className="w-6 h-6" />
                  <span className="text-xs uppercase tracking-widest">Account</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;